import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMyGames, rematchGame } from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { GameListRow, PlayingBanner, PlayingEmptyState, PlayingSkeleton } from './playingUi';
import { getActiveRole, getRoles, getUserId } from '../../utils/auth';
import { apiErrorMessage } from '../../utils/apiError';

const PAGE_SIZE = 50;
const FINISHED = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW', 'ABORTED']);

function isParentViewer() {
  return String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
}

function isActiveGame(g) {
  const st = String(g?.status || '').toUpperCase();
  if (g?.endedAt) return false;
  return st === 'ACTIVE' || st === 'PENDING';
}

function isFinishedGame(g) {
  if (g?.endedAt) return true;
  return FINISHED.has(String(g?.status || '').toUpperCase());
}

function canRematchFromList(g) {
  if (isParentViewer()) return false;
  if (!isFinishedGame(g)) return false;
  if (String(g?.gameType || '').toUpperCase() === 'VS_COMPUTER') return false;
  if (String(g?.status || '').toUpperCase() === 'ABORTED') return false;
  const me = Number(getUserId());
  if (!me) return false;
  return Number(g.whiteUserId) === me || Number(g.blackUserId) === me;
}

/** Flip-color AI rematch deep link from a finished VS_COMPUTER list row. */
function computerPlayAgainHref(g) {
  if (!isFinishedGame(g)) return undefined;
  if (String(g?.gameType || '').toUpperCase() !== 'VS_COMPUTER') return undefined;
  const me = Number(getUserId());
  if (!me) return undefined;
  const wasWhite = Number(g.whiteUserId) === me;
  const wasBlack = Number(g.blackUserId) === me;
  if (!wasWhite && !wasBlack) return undefined;
  const color = wasWhite ? 'black' : 'white';
  const level = g.computerLevel || 3;
  const rated = g.rated ? '1' : '0';
  const tc = g.timeControlSeconds || 600;
  const inc = g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0;
  return `/playing/play?color=${color}&level=${level}&rated=${rated}&tc=${tc}&inc=${inc}`;
}

function isCorrGame(g) {
  return g.daysPerMove != null && Number(g.daysPerMove) > 0;
}

function deadlineMs(g) {
  if (!g?.moveDeadlineAt) return Number.POSITIVE_INFINITY;
  const t = Date.parse(g.moveDeadlineAt);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

function sortByDeadline(rows) {
  return [...rows].sort((a, b) => deadlineMs(a) - deadlineMs(b));
}

function normalizePage(data) {
  if (Array.isArray(data)) {
    return { games: data, page: 0, hasMore: false, total: data.length };
  }
  return {
    games: Array.isArray(data?.games) ? data.games : [],
    page: data?.page ?? 0,
    hasMore: !!data?.hasMore,
    total: data?.total ?? 0,
  };
}

export default function MyGamesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = (() => {
    const f = String(searchParams.get('filter') || '').toUpperCase();
    // Ignore legacy CLUB / tournamentId query params (feature removed).
    return ['ALL', 'ACTIVE', 'CORR', 'YOUR_MOVE', 'PAST'].includes(f) ? f : 'ALL';
  })();
  const [games, setGames] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(initialFilter); // ALL | ACTIVE | CORR | YOUR_MOVE | PAST
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [rematchBusyId, setRematchBusyId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const selectFilter = (id) => {
    setFilter(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'ALL') next.delete('filter');
    else next.set('filter', id);
    next.delete('tournamentId');
    setSearchParams(next, { replace: true });
  };

  const changeSearch = (value) => {
    setSearch(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (!trimmed) next.delete('q');
    else next.set('q', trimmed);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const f = String(searchParams.get('filter') || '').toUpperCase();
    if (['ALL', 'ACTIVE', 'CORR', 'YOUR_MOVE', 'PAST'].includes(f)) setFilter(f);
    else if (!searchParams.get('filter') || f === 'CLUB') setFilter('ALL');
    const q = searchParams.get('q') || '';
    setSearch((prev) => (prev === q ? prev : q));
    if (searchParams.get('tournamentId') || f === 'CLUB') {
      const next = new URLSearchParams(searchParams);
      next.delete('tournamentId');
      if (f === 'CLUB') next.delete('filter');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const applyPage = useCallback((data, append) => {
    const norm = normalizePage(data);
    setGames((prev) => {
      if (!append) return norm.games;
      const seen = new Set(prev.map((g) => g.id));
      return [...prev, ...norm.games.filter((g) => !seen.has(g.id))];
    });
    setPage(norm.page);
    setHasMore(norm.hasMore);
    setTotal(norm.total);
  }, []);

  useEffect(() => {
    setLoading(true);
    getMyGames(0, PAGE_SIZE)
      .then((data) => {
        applyPage(data, false);
        setError('');
      })
      .catch(() => setError(t('playing.loadFailed')))
      .finally(() => setLoading(false));
  }, [t, applyPage, reloadTick]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await getMyGames(page + 1, PAGE_SIZE);
      applyPage(data, true);
    } catch {
      setError(t('playing.loadFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  const counts = useMemo(() => {
    const active = games.filter(isActiveGame);
    return {
      ALL: games.length,
      ACTIVE: active.length,
      YOUR_MOVE: active.filter((g) => g.myTurn).length,
      CORR: active.filter(isCorrGame).length,
      PAST: games.filter((g) => !isActiveGame(g)).length,
    };
  }, [games]);

  const filtered = useMemo(() => {
    let rows;
    switch (filter) {
      case 'ACTIVE':
        rows = sortByDeadline(games.filter(isActiveGame));
        break;
      case 'CORR':
        rows = sortByDeadline(games.filter((g) => isActiveGame(g) && isCorrGame(g)));
        break;
      case 'YOUR_MOVE':
        rows = sortByDeadline(games.filter((g) => isActiveGame(g) && g.myTurn));
        break;
      case 'PAST':
        rows = games.filter((g) => !isActiveGame(g));
        break;
      default:
        rows = games;
    }
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((g) => {
      const white = String(g.whiteName || '').toLowerCase();
      const black = String(g.blackName || '').toLowerCase();
      const status = String(g.status || '').toLowerCase();
      const statusLabel = String(t(`playing.status.${g.status}`, { defaultValue: g.status }) || '').toLowerCase();
      return white.includes(q) || black.includes(q) || status.includes(q) || statusLabel.includes(q);
    });
  }, [games, filter, search, t]);

  const active = filtered.filter(isActiveGame);
  const past = filtered.filter((g) => !isActiveGame(g));
  const showSplit = filter === 'ALL';

  const onRematch = async (g) => {
    if (!g?.id || rematchBusyId) return;
    setRematchBusyId(g.id);
    try {
      const ch = await rematchGame(g.id);
      if (ch?.gameId) {
        navigate(`/playing/game/${ch.gameId}`);
      } else {
        setError('');
        navigate('/playing');
      }
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.rematchFailed')));
    } finally {
      setRematchBusyId(null);
    }
  };

  const rowExtras = (g) => ({
    analyzeHref: isFinishedGame(g) && String(g.status || '').toUpperCase() !== 'ABORTED'
      ? `/playing/game/${g.id}?analyze=1`
      : undefined,
    playAgainHref: computerPlayAgainHref(g),
    onRematch: canRematchFromList(g) ? () => onRematch(g) : undefined,
    rematchBusy: rematchBusyId === g.id,
  });

  const filters = [
    { id: 'ALL', label: t('playing.filterAll') },
    { id: 'ACTIVE', label: t('playing.activeGames') },
    { id: 'YOUR_MOVE', label: t('playing.yourMove') },
    { id: 'CORR', label: t('playing.correspondenceGames') },
    { id: 'PAST', label: t('playing.pastGames') },
  ];

  const loadMoreBtn = hasMore ? (
    <div className="mt-4 flex justify-center">
      <Button variant="page-secondary" disabled={loadingMore} onClick={loadMore}>
        {loadingMore
          ? t('playing.loading')
          : t('playing.loadMoreGames', {
            defaultValue: 'Load more ({{loaded}} of {{total}})',
            loaded: games.length,
            total,
          })}
      </Button>
    </div>
  ) : null;

  return (
    <div className="page-shell-form playing-page">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.myGames')}
        subtitle={t('playing.myGamesSubtitle')}
        aside={(
          <Button variant="page-primary" onClick={() => navigate('/playing')}>
            {t('playing.playNow')}
          </Button>
        )}
      />
      {error && (
        <PlayingBanner tone="danger">
          <p>{error}</p>
          <div className="mt-2">
            <Button variant="page-secondary" onClick={() => setReloadTick((n) => n + 1)}>
              {t('playing.retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        </PlayingBanner>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
              filter === f.id
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
            onClick={() => selectFilter(f.id)}
            data-testid={`playing-games-filter-${f.id}`}
          >
            {f.label}
            {counts[f.id] > 0 ? ` (${counts[f.id]})` : ''}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <label className="sr-only" htmlFor="playing-games-search">
          {t('playing.searchGames', { defaultValue: 'Search games' })}
        </label>
        <input
          id="playing-games-search"
          type="search"
          className="input max-w-md"
          placeholder={t('playing.searchGamesPlaceholder', {
            defaultValue: 'Filter by opponent name or status…',
          })}
          value={search}
          onChange={(e) => changeSearch(e.target.value)}
          data-testid="playing-games-search"
        />
      </div>

      {loading ? (
        <div className="list-table-card p-4">
          <PlayingSkeleton rows={5} />
        </div>
      ) : error ? null : filtered.length === 0 ? (
        <div className="list-table-card">
          <PlayingEmptyState
            title={search.trim()
              ? t('playing.noGamesMatchSearch', { defaultValue: 'No games match this search' })
              : filter === 'ALL'
                ? t('playing.noGames')
                : t('playing.noGamesMatchFilter')}
            hint={search.trim()
              ? t('playing.noGamesMatchSearchHint', { defaultValue: 'Try another name or clear the search.' })
              : filter === 'ALL'
                ? t('playing.noGamesHint')
                : t('playing.noGamesMatchFilterHint')}
            action={search.trim() ? (
              <Button variant="page-secondary" onClick={() => changeSearch('')}>
                {t('playing.clearSearch', { defaultValue: 'Clear search' })}
              </Button>
            ) : filter === 'ALL' ? (
              <Button variant="page-primary" onClick={() => navigate('/playing')}>
                {t('playing.playNow')}
              </Button>
            ) : (
              <Button variant="page-secondary" onClick={() => selectFilter('ALL')}>
                {t('playing.filterAll')}
              </Button>
            )}
          />
        </div>
      ) : showSplit ? (
        <div className="space-y-6">
          {active.length > 0 && (
            <section className="list-table-card overflow-hidden">
              <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3">
                <h2 className="text-sm font-semibold text-slate-800">{t('playing.activeGames')}</h2>
              </div>
              <ul className="divide-y divide-slate-100">
                {active.map((g) => (
                  <GameListRow
                    key={g.id}
                    game={g}
                    href={`/playing/game/${g.id}`}
                    actionLabel={g.myTurn ? t('playing.yourMove') : t('playing.open')}
                    {...rowExtras(g)}
                  />
                ))}
              </ul>
            </section>
          )}
          <section className="list-table-card overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-800">{t('playing.pastGames')}</h2>
            </div>
            {past.length === 0 ? (
              <PlayingEmptyState
                title={t('playing.noPastGames')}
                action={(
                  <Button variant="page-secondary" onClick={() => navigate('/playing')}>
                    {t('playing.playNow')}
                  </Button>
                )}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {past.map((g) => (
                  <GameListRow
                    key={g.id}
                    game={g}
                    href={`/playing/game/${g.id}`}
                    actionLabel={t('playing.review')}
                    {...rowExtras(g)}
                  />
                ))}
              </ul>
            )}
          </section>
          {loadMoreBtn}
        </div>
      ) : (
        <div className="list-table-card overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {filtered.map((g) => (
              <GameListRow
                key={g.id}
                game={g}
                href={`/playing/game/${g.id}`}
                actionLabel={isActiveGame(g)
                  ? (g.myTurn ? t('playing.yourMove') : t('playing.open'))
                  : t('playing.review')}
                {...rowExtras(g)}
              />
            ))}
          </ul>
          {loadMoreBtn}
        </div>
      )}
    </div>
  );
}
